<template>
  <div class="container">
    <div v-if="status === rs.readyToRecord || status === rs.isRecording" class="record-container">
      <div class="record-title">
        <div class="recording"></div>
        {{ title }}
      </div>
      <div class="record-button" @click="recordAudio">
        <img
          alt="Record audio"
          :class="{
            'record-image': status === 'readyToRecord',
            'stop-image': status === 'isRecording'
          }"
          :src="actionIcon"
        />
      </div>
    </div>
    <div v-else class="recorded-container">
      <div class="recorded-row">
        <img
          v-if="!isLoadingFile && !isDrawingWave"
          alt="Play / Pause"
          class="recorded-play"
          :src="playIcon"
          @click="playAudio"
        />
        <div class="recorded-waveform">
          <div id="recordAudioWave" class="waveform"></div>
        </div>
        <div v-if="!isLoadingFile && !isDrawingWave">
          {{ recordAudioPlaybackTime }}
        </div>
        <img
          v-if="!isLoadingFile && !isDrawingWave"
          alt="Delete file"
          class="recorded-delete"
          src="@/assets/images/delete4.svg"
          @click="deleteAudio"
        />
      </div>
      <div v-if="!isLoadingFile && !isDrawingWave">
        <div class="recorded-title">
          Recorded successfully!
        </div>
        <span class="recorded-description">
          If you would like to record a new message, please click the
          <img src="@/assets/images/delete4.svg" alt="Delete file" /> icon, then start a new recording.
        </span>
      </div>
      <span v-if="isLoadingFile" class="recorded-description">
        Loading audio ({{ recordAudioLoadingPercentage }}%)...
      </span>
      <span v-if="isDrawingWave" class="recorded-description">
        Building audio spectrogram...
      </span>
    </div>
  </div>
</template>

<script>
import WaveSurfer from 'wavesurfer';
import { createAudioRecorder, formatMMSS } from '@/utils';

// recording statuses
const rs = {
  isProcessing: 'isProcessing',
  isRecording: 'isRecording',
  processed: 'processed',
  readyToRecord: 'readyToRecord',
  recorded: 'recorded'
};

export default {
  name: 'PlayRecordingRecordAudio',
  props: {
    setError: Function,
    recordedFile: Blob,
    recordedDuration: Number,
    initalLoading: Boolean
  },
  data() {
    return {
      isDrawingWave: false,
      isLoadingFile: false,
      recordAudioElapsedPlaybackTime: 0,
      recordAudioElapsedRecordingTime: 0,
      recordAudioIsPlaying: false,
      recordAudioLoadingPercentage: 0,
      recordAudioPlaybackTimer: null,
      recordAudioRecordingTimer: null,
      recorded: null,
      duration: 0,
      rs,
      status: rs.readyToRecord,
      wave: null
    };
  },
  mounted() {
    if (this.recordedFile) {
      this.recorded = this.recordedFile;
      this.duration = this.recordedDuration;
      this.showTheWave();
    }
  },
  computed: {
    /**
     * Load the icon based on the recording status
     */
    actionIcon() {
      const image = require.context('@/assets/images', false, /\.svg$/);
      return image(`./${this.status === this.rs.readyToRecord ? 'record' : 'stop1'}.svg`);
    },
    /**
     * Play / pause button icon
     */
    playIcon() {
      const images = require.context('@/assets/images', false, /\.svg$/);
      return images(`./${this.recordAudioIsPlaying ? 'stop' : 'play'}.svg`);
    },
    /**
     * Compute the title based on the recording status
     * @returns {string}
     */
    title() {
      if (this.status === this.rs.readyToRecord) {
        return 'Start recording';
      }
      if (this.status === this.rs.isRecording) {
        return this.recordAudioRecordingTime;
      }
      return '';
    },
    recordAudioPlaybackTime() {
      return formatMMSS(this.recordAudioIsPlaying ? this.recordAudioElapsedPlaybackTime : this.duration);
    },
    recordAudioRecordingTime() {
      return formatMMSS(this.recordAudioElapsedRecordingTime);
    }
  },
  watch: {
    initalLoading() {
      if (this.recordedFile) {
        this.recorded = this.recordedFile;
        this.duration = this.recordedDuration;
        this.showTheWave();
      }
    }
  },
  methods: {
    /**
     * Stop audio playback
     */
    stopPlayback() {
      if (this.wave) {
        // destroy the WaveSurfer instance
        this.wave.destroy();
      }

      // set playback status, delete playback timer
      this.recordAudioIsPlaying = false;
      clearInterval(this.recordAudioPlaybackTimer);

      // delete audio recording timer (just in case...)
      this.recordAudioElapsedRecordingTime = 0;
      clearInterval(this.recordAudioRecordingTimer);

      // clear display values
      this.recordAudioElapsedPlaybackTime = 0;
    },
    /**
     * Delete recorded audio
     */
    async deleteAudio() {
      // stop the playback first
      this.stopPlayback();

      // delete the file link
      this.recorded = null;
      this.$emit('delete-audio');
      this.$emit('enable-save', false);

      // set recording status
      this.status = this.rs.readyToRecord;
    },
    /**
     * Play / pause recorded audio
     */
    playAudio() {
      if (!this.recordAudioIsPlaying) {
        this.wave.play();

        // show recording time
        this.recordAudioPlaybackTimer = setInterval(() => {
          this.recordAudioElapsedPlaybackTime += 1;
        }, 1000);

        this.recordAudioIsPlaying = true;
      } else {
        // delete playback timer
        clearInterval(this.recordAudioPlaybackTimer);
        this.recordAudioPlaybackTimer = null;

        // clear elapsed playback time, pause the playback
        this.recordAudioElapsedPlaybackTime = 0;
        this.wave.pause();

        this.recordAudioIsPlaying = false;
      }
    },
    /**
     * Start / stop audio recording
     * @returns {Promise<*>|void}
     */
    async recordAudio() {
      try {
        this.setError();
        // on start recording
        if (this.status === this.rs.readyToRecord) {
          // create recorder instance & start recording
          this.recordAudioElapsedRecordingTime = 0;
          this.recorder = await createAudioRecorder();
          this.recorder.start();

          // show recording time
          this.recordAudioRecordingTimer = setInterval(() => {
            this.recordAudioElapsedRecordingTime += 1;
          }, 1000);

          this.$emit('enable-save', false);

          // update recording status
          return (this.status = this.rs.isRecording);
        }
        // on finish recording
        if (this.status === this.rs.isRecording) {
          // delete recording timer
          clearInterval(this.recordAudioRecordingTimer);
          this.recordAudioRecordingTimer = null;

          // clear elapsed recording time, set new recording status
          this.status = this.rs.recorded;
          // process the recorded audio
          const { audioBlob } = await this.recorder.stop();
          this.recorded = audioBlob;
          this.duration = this.recordAudioElapsedRecordingTime;
          this.$emit('store-audio', audioBlob, Math.floor(this.recordAudioElapsedRecordingTime));
          return this.showTheWave();
        }
      } catch (error) {
        this.setError(error.message || error);
      }
    },
    /**
     * Show WaveSurfer audio wave
     * @param audioURL {string} - recorded audio URL
     */
    showTheWave() {
      this.status = this.rs.isProcessing;
      return this.$nextTick(async function() {
        try {
          const reader = new FileReader();
          this.isLoadingFile = true;
          this.isDrawingWave = false;

          reader.onload = (file) => {
            try {
              // convert file to Blob
              const blob = new Blob([new Uint8Array(file.target.result)]);

              // create a WaveSurfer instance for the "Record audio" tab
              this.wave = WaveSurfer.create({
                container: '#recordAudioWave',
                waveColor: '#C2C7CF',
                progressColor: '#606C82',
                height: 70,
                barWidth: 1,
                barGap: 2,
                interact: false
              });

              // load Blob
              this.wave.loadBlob(blob);
              this.recordAudioLoadingPercentage = 0;

              // track loading progress
              this.wave.on('loading', (percent = 0) => {
                this.recordAudioLoadingPercentage = percent;
                if (Number(percent) === 100) {
                  this.isLoadingFile = false;
                  this.isDrawingWave = true;
                }
              });

              // track waveform drawing
              this.wave.on('ready', () => {
                this.isDrawingWave = false;
                this.status = this.rs.processed;
                this.$emit('enable-save', true);
              });

              // handle end of playback event
              this.wave.on('finish', () => {
                // delete playback timer
                clearInterval(this.recordAudioPlaybackTimer);

                // clear elapsed playback time, pause the playback
                this.wave.stop();
                this.recordAudioElapsedPlaybackTime = 0;
                this.recordAudioIsPlaying = false;
              });
            } catch (error) {
              console.log('-- PLAY RECORDING -> RECORD AUDIO: ERROR\n', error);
              this.setError(error);
            }
          };

          reader.onerror = (error) => {
            console.log('-- PLAY RECORDING -> RECORD AUDIO: ERROR\n', error);
            this.setError(error);
          };

          // read file as an ArrayBuffer
          return reader.readAsArrayBuffer(this.recorded);
        } catch (error) {
          // await this.clearStoredFile();
          console.log('-- PLAY RECORDING -> RECORD AUDIO: ERROR\n', error);
          this.setError(error);
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.container {
  width: 100%;
  padding: 0 40px;
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 3px;
}
.record-container {
  width: 100%;
  height: 125px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #fafbfc;
  border-radius: 3px;
  .record-title-image {
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.02em;
    color: #374561;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .record-title {
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
    letter-spacing: -0.02em;
    color: #374561;
    display: flex;
    flex-direction: row;
    align-items: center;
    .recording {
      width: 10px;
      height: 10px;
      border-radius: 5px;
      background-color: #cc2f2f;
      margin-right: 5px;
    }
  }
  .record-button {
    margin-top: 5px;
    width: 55px;
    height: 55px;
    border-radius: 50%;
    background-color: #77a9d7;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    .record-image {
      width: 19px;
      height: 27px;
    }
    .stop-image {
      width: 19px;
      height: 19px;
    }
  }
}
.recorded-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  .recorded-row {
    position: relative;
    height: 50px;
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: #f4f5f7;
    border-radius: 3px;
    padding: 0 20px;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: #000000;
  }
  .recorded-play {
    width: 13.33px;
    height: 16.67px;
    cursor: pointer;
  }
  .recorded-waveform {
    flex: 1;
    height: 50px;
    max-width: 380px;
    display: block;
    margin: 0 5px;
    overflow: hidden;
    .waveform {
      transform: translate(0, -10px);
    }
  }
  .recorded-delete {
    position: absolute;
    right: 12px;
    cursor: pointer;
  }
  .recorded-title {
    margin-top: 15px;
    font-style: normal;
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: #374561;
  }
  .recorded-description {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: $colorPlaceholder;
    margin-top: 15px;
    width: 450px;
    .recorded-description-delete {
      width: 12.5px;
      height: 13.75px;
    }
  }
}
</style>
